/* Å høre på en time. Blobben, minuttene, og spilleren.
 *
 * ---------------------------------------------------------------------------
 * HVORFOR DETTE ER EN EGEN FIL
 *
 * Ledningene her er ikke opplagte, og de er stille når de ryker. `startEpoch`
 * gjør spillehodet til et øyeblikk i stedet for et tall inn i en fil; `tapp`
 * lar blobben bygge sitt eget båndpass på spillerens kilde uten at spilleren
 * vet hva den brukes til; og lyden får IKKE lov til å starte av seg selv, fordi
 * nettleseren bare gir en AudioContext lov til å starte fra en gest og
 * hentingen mellom klikket og lyden avslutter gesten. Tre regler som er lette å
 * miste i en kopi, og som ingen test fanger.
 *
 * ---------------------------------------------------------------------------
 * TO SIDER, ETT OPPSETT, TO FORSKJELLIGE RAMMER
 *
 * Loggen og loggboka spør ulike spørsmål og trenger den samme lyden. Forskjellen
 * er hvor blobben står: loggen tegner den øverst sammen med spekteret, i
 * `.timen`, mens minuttene og spilleren kommer etter peiling, kart og forhold.
 * Loggboka har alt i ett panel.
 *
 * Derfor `blob` og `onSource`. Med `blob={false}` tegner ikke denne sin egen
 * blob, og kalleren sender inn sin egen `onSource` slik at DENS blob får tappe
 * spillerens kilde. Ledningen krysser komponentgrensen én gang, med et navn,
 * framfor at hele oppsettet skrives to ganger.
 *
 * ---------------------------------------------------------------------------
 * UTEN KALIBRERING SPILLER DEN IKKE
 *
 * R5.3: et nivå uten kalibrering er stedsrelativt, og spilleren skal ikke få en
 * oppdiktet en. Sagt som en setning framfor å skjule knappen -- den som lurer
 * på hvorfor det ikke går an å høre, skal få vite det.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import type { Calibration, HourMedia, LogSensor, StoredObject } from '../api'
import { ClipPlayer } from './ClipPlayer'
import { Lydblob } from './Lydblob'
import { newPlayhead, type Playhead } from '../playhead'

/** Et minutt fra timen, som det objektet spilleren forventer.
 *
 *  Én funksjon framfor to like objektlitteraler, fordi feltene her er
 *  spillerens kontrakt og ikke noe denne filen finner på. */
function objektFor(m: HourMedia): StoredObject {
  return {
    key: m.key, kind: m.kind, ts: m.start_utc ?? '', size: m.size,
    md5: null, state: 'stored', attribution_conflict: false,
    presigned_at: m.start_utc ?? '', stored_at: m.start_utc,
    start_utc: m.start_utc,
  }
}

export function Timelyd({
  media, sensors, calibration, ph, straks, blob = true, onSource,
}: {
  media: HourMedia[]
  sensors: LogSensor[]
  calibration: Record<string, Calibration>
  /** Spillehodet. Valgfritt: den som bare vil høre, skal slippe å finne på et.
   *  Loggen sender sitt eget inn, fordi peilingen og kartet tegner fra det
   *  samme. */
  ph?: Playhead
  /** Last det første minuttet med én gang. For en rad som nettopp ble åpnet av
   *  et trykk -- spilleren står da lastet og klar, ikke i gang. */
  straks?: boolean
  /** Tegn blobben her. `false` for en kaller som har sin egen et annet sted i
   *  oppsettet -- loggen har den oppe ved spekteret. */
  blob?: boolean
  /** Kilden, når spilleren har en. For kalleren som tegner sin egen blob og
   *  trenger å tappe den. Uten denne kobler komponenten sin egen. */
  onSource?: (ctx: AudioContext, kilde: AudioNode) => void
}) {
  const egen = useRef(newPlayhead())
  const hode = ph ?? egen.current

  const [spiller, setSpiller] =
    useState<{ objekt: StoredObject; oppdrag: string } | null>(null)

  /* Blobben ber om kilden når spilleren har en. Holdt i en ref slik at
     spilleren får den samme funksjonen hver gang og lydgrafen ikke bygges om
     for at en visualisering skal montere seg. */
  const tapp = useRef<((ctx: AudioContext, kilde: AudioNode) => void) | null>(null)
  const settTapp = useCallback(
    (f: (ctx: AudioContext, kilde: AudioNode) => void) => { tapp.current = f }, [])
  const egenKilde = useCallback(
    (ctx: AudioContext, kilde: AudioNode) => tapp.current?.(ctx, kilde), [])
  const paaKilde = onSource ?? egenKilde

  const minutter = media.filter((m) => m.kind === 'reduced')
  const forste = minutter[0]

  useEffect(() => {
    if (!straks || !forste || spiller !== null) return
    hode.startEpoch = forste.start_utc ? Date.parse(forste.start_utc) / 1000 : 0
    setSpiller({ oppdrag: forste.mission_id, objekt: objektFor(forste) })
    // Bare på nøkkelen: et nytt minutt er en ny lyd, en ny render er ikke det.
  }, [straks, forste?.key])

  const sterkeste = sensors.reduce<LogSensor | null>(
    (a, s) => (a === null || (s.score ?? 0) > (a.score ?? 0) ? s : a), null)
  const kalibrering: Calibration | null = spiller
    ? (calibration[spiller.oppdrag] ?? null) : null

  if (!minutter.length) {
    /* Fraværet sagt høyt. Et tomt felt leses som «ikke sjekket», og for en time
       ingen lyd er lagret for er nettopp fraværet det leseren må vite. */
    return <p className="fjernt">ingen lyd lagret for denne timen</p>
  }

  return (
    <>
      {blob && sterkeste
        && <Lydblob sensor={sterkeste} ph={hode} tapp={settTapp} />}

      <div className="min-stripe">
        {minutter.map((m) => {
          const tt = m.start_utc ? new Date(m.start_utc) : null
          return (
            <button key={m.key} className="min" type="button"
              aria-pressed={spiller?.objekt.key === m.key}
              onClick={() => {
                /* Klippets eget starttidspunkt. Uten det er spillehodet et tall
                   inn i en fil; med det er det et øyeblikk en båt var et sted. */
                hode.startEpoch = m.start_utc
                  ? Date.parse(m.start_utc) / 1000 : 0
                setSpiller({ oppdrag: m.mission_id, objekt: objektFor(m) })
              }}>
              {tt ? String(tt.getUTCMinutes()).padStart(2, '0') : '··'}
            </button>
          )
        })}
      </div>

      {spiller && kalibrering && (
        <ClipPlayer object={spiller.objekt} calibration={kalibrering}
          playhead={hode} onSource={paaKilde}
          onClose={() => setSpiller(null)} />
      )}
      {spiller && !kalibrering && (
        <p className="notice bad">Uten kalibrering.</p>
      )}
    </>
  )
}
